import resolveServiceWorkerContainer from './resolveServiceWorkerContainer';
import unregisterServiceWorker from './unregisterServiceWorker';
import parameters from '../../parameters';

export type RegisterServiceWorkerOptions = {
	readonly container?: ServiceWorkerContainer;
	readonly isEnabled?: boolean;
	readonly scriptUrl?: string;
};

const DEFAULT_SCRIPT_URL = '/service-worker.js';
const DEFAULT_SCOPE = '/';

/**
 * `isEnabled` defaults to `parameters.pwa.serviceWorkerEnabled`, resolved at call time rather than
 * captured at module load: `parameters` is frozen from `import.meta.env.MODE`, which is `test` under
 * Vitest, so reading it eagerly would make the registering branch permanently unreachable in tests.
 *
 * A browser without the container simply boots without offline support (no throw). Outside
 * production, any leftover worker is actively unregistered rather than left to intercept the dev
 * server. A registration failure is swallowed: installability is progressive enhancement, and
 * failing application startup over it would be a worse defect than the missing enhancement itself.
 */
const registerServiceWorker = (options: RegisterServiceWorkerOptions = {}): void => {
	const container = options.container ?? resolveServiceWorkerContainer();
	const isEnabled = options.isEnabled ?? parameters.pwa.serviceWorkerEnabled;
	const scriptUrl = options.scriptUrl ?? DEFAULT_SCRIPT_URL;

	if (!container) {
		return;
	}

	if (!isEnabled) {
		unregisterServiceWorker({ container });
		return;
	}

	window.addEventListener('load', () => {
		container.register(scriptUrl, { scope: DEFAULT_SCOPE, updateViaCache: 'none' }).catch(() => {
			// A browser that refuses to install the worker must still run the application normally.
		});
	});
};

export default registerServiceWorker;
